import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";

export default function PromptModalUi() {
  const [prompts, setPrompts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState({
    title: "",
    previousContent: "",
    updatedContent: "",
    description: "",
  });
  const [response, setResponse] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [promptsLoading, setPromptsLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Function to fetch prompts from the server
  const fetchPrompts = async () => {
    try {
      setPromptsLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/prompts/get-prompts`
      );
      console.log("🚀 ~ fetchPrompts ~ res:", res);
      // Check if the response has data property (for when get-prompts returns data inside data)
      const responseData = res.data.data ? res.data.data : res.data;
      setPrompts(Array.isArray(responseData) ? responseData : []);
    } catch (error) {
      setErrorMessage("Error fetching prompts");
      setPrompts([]);
    } finally {
      setPromptsLoading(false);
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, []);

  const openModal = (title, content) => {
    // Find the prompt group by title
    const promptGroup = prompts.find(p => p.title === title);
    
    // Get the description from promptDescriptions if available
    let description = "";
    if (promptGroup && promptGroup.promptDescriptions) {
      description = promptGroup.promptDescriptions[content] || "";
    }

    setSelectedPrompt({
      title,
      previousContent: content,
      updatedContent: content,
      description,
    });

    setIsOpen(true);
    setResponse("");
  };

  const handleTryClick = async () => {
    setIsLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/prompts/generate-prompts`,
        {
          prompt: selectedPrompt.updatedContent,
        }
      );
      setResponse(res.data.response);
    } catch (error) {
      setErrorMessage("Failed to generate response. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveClick = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/prompts/save-prompt`,
        {
          previousPrompt: selectedPrompt.previousContent,
          updatedPrompt: selectedPrompt.updatedContent,
          description: selectedPrompt.description, // Include the description in the request
        }
      );

      // After successful save, fetch fresh data to ensure we have the latest
      await fetchPrompts();
      
      setSuccessMessage("Prompt saved successfully");
      setIsOpen(false);
    } catch (error) {
      console.error("Error saving prompt:", error);
      setErrorMessage("Error saving prompt");
    }
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage("");
    setErrorMessage("");
  };

  return (
    <div className="app-page">
    <Container maxWidth={false} disableGutters sx={{ fontFamily: "Poppins, sans-serif" }}>
      <Typography
        className="app-page-title"
        variant="h4"
        component="h2"
        gutterBottom
        sx={{ mb: 3, display: "block" }}
      >
        Support Prompts
      </Typography>
      <Box sx={{ height: "24px" }} />
      {promptsLoading ? (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          sx={{ py: 5 }}
        >
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 3, color: "text.secondary" }}>
            Loading support prompts...
          </Typography>
        </Box>
      ) : prompts.length === 0 ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ py: 4 }}
        >
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            No prompts found.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {prompts.map((prompt, index) => (
            <Grid item xs={12} key={index}>
              <Card elevation={3} sx={{ borderRadius: "10px", overflow: "hidden" }}>
                <CardContent className="p-0">
                  <div className="CardHeaderBox">
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: 600, fontSize: "11px", letterSpacing: "0.6px", textTransform: "uppercase", color: "#fff", mb: 0 }}
                    >
                      {prompt.title}
                    </Typography>
                  </div>
                  <div className="CardInnerBox">
                    {prompt.options.map((option, idx) => (
                      <Box
                        key={idx}
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={1}
                      >
                        <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                          {/* Show the description instead of the option text */}
                          {prompt.promptDescriptions[option]}
                        </Typography>
                        <button
                          className="primaryBtn"
                          onClick={() => openModal(prompt.title, option)}
                          sx={{ borderRadius: 2 }}
                        >
                          Edit
                        </button>
                      </Box>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          className="modalHeaderDark"
          sx={{ fontWeight: "bold", fontSize: "1.5rem" }}
        >
          {selectedPrompt.title}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Description"
            value={selectedPrompt.description || ""}
            margin="normal"
            onChange={(e) =>
              setSelectedPrompt((prev) => ({ ...prev, description: e.target.value }))
            }
            sx={{ fontSize: "1rem" }}
          />

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Edit your prompt"
            value={selectedPrompt.updatedContent}
            onChange={(e) =>
              setSelectedPrompt({
                ...selectedPrompt,
                updatedContent: e.target.value,
              })
            }
            margin="normal"
            sx={{ fontSize: "1rem" }}
          />
          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{ mt: 2 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Response"
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              margin="normal"
              sx={{ fontSize: "1rem" }}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleTryClick}
            variant="contained"
            color="success"
            sx={{ borderRadius: 2, fontWeight: "bold" }}
          >
            Try
          </Button>
          <Button
            onClick={handleSaveClick}
            variant="contained"
            color="secondary"
            sx={{ borderRadius: 2, fontWeight: "bold" }}
          >
            Save
          </Button>
          <Button
            onClick={() => setIsOpen(false)}
            variant="outlined"
            color="error"
            sx={{ borderRadius: 2, fontWeight: "bold" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
    </div>
  );
}
